<ng-template #styleProps let-group="group" let-isDefault="isDefault">
  <div [formGroup]="group" class="d-flex flex-column gap-2">
    <div class="style-section">
      <span class="mat-body-small style-section-title">Style</span>
      <div class="row g-2 align-items-center">
        <div class="col-auto d-flex align-items-center gap-1">
          <span class="mat-body-small">Fill</span>
          <lib-color-swatch-picker
            [color]="group.get('fillColor').value"
            outputFormat="rgba"
            alphaChannel="forced"
            [presetColors]="usedColors"
            (colorChange)="group.get('fillColor').setValue($event)"
            (colorApply)="addToUsedColors($event)" />
        </div>

        <div class="col-auto d-flex align-items-center gap-1">
          <span class="mat-body-small">Stroke</span>
          <lib-color-swatch-picker
            [color]="group.get('strokeColor').value"
            outputFormat="rgba"
            alphaChannel="forced"
            [presetColors]="usedColors"
            (colorChange)="group.get('strokeColor').setValue($event)"
            (colorApply)="addToUsedColors($event)" />
        </div>

        <div class="col-auto">
          <mat-form-field appearance="outline" class="form-ultra-slim field-width" subscriptSizing="dynamic">
            <mat-label>Width</mat-label>
            <input matInput type="number" formControlName="strokeWidth" min="0" />
          </mat-form-field>
        </div>

        <div class="col-auto">
          <mat-form-field appearance="outline" class="form-ultra-slim field-width-lg" subscriptSizing="dynamic">
            <mat-label>Line</mat-label>
            <mat-select formControlName="strokeDashPreset">
              @for (preset of dashPresetOptions; track preset.value) {
                <mat-option [value]="preset.value" [disabled]="preset.value === 'custom'">{{ preset.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>

    @if (hasPointFeatures) {
      <div class="style-section">
        <div class="d-flex align-items-center gap-2">
          <mat-checkbox formControlName="includeCircle">Point marker</mat-checkbox>
          <span class="mat-body-small text-muted">Used for point-shaped features</span>
        </div>

        @if (group.get('includeCircle').value) {
          <div class="row g-2 align-items-center mt-1">
            <div class="col-auto">
              <mat-form-field appearance="outline" class="form-ultra-slim field-width" subscriptSizing="dynamic">
                <mat-label>Radius</mat-label>
                <input matInput type="number" formControlName="circleRadius" min="1" />
              </mat-form-field>
            </div>

            <div class="col-auto d-flex align-items-center gap-1">
              <span class="mat-body-small">Marker fill</span>
              <lib-color-swatch-picker
                [color]="group.get('circleFillColor').value"
                outputFormat="rgba"
                alphaChannel="forced"
                [presetColors]="usedColors"
                (colorChange)="group.get('circleFillColor').setValue($event)"
                (colorApply)="addToUsedColors($event)" />
            </div>

            <div class="col-auto d-flex align-items-center gap-1">
              <span class="mat-body-small">Marker stroke</span>
              <lib-color-swatch-picker
                [color]="group.get('circleStrokeColor').value"
                outputFormat="rgba"
                alphaChannel="forced"
                [presetColors]="usedColors"
                (colorChange)="group.get('circleStrokeColor').setValue($event)"
                (colorApply)="addToUsedColors($event)" />
            </div>
          </div>
        }
      </div>
    }

    <div class="style-section" [formGroup]="group.get('text')">
      <div class="d-flex align-items-center gap-2">
        @if (isDefault || !labelAppliesToAll) {
          <mat-checkbox formControlName="enabled">Label (text)</mat-checkbox>
        } @else {
          <mat-checkbox [checked]="form.get('default').get('text').get('enabled').value" [disabled]="true">Label (text)</mat-checkbox>
          <span class="mat-body-small text-muted">Inherited from Default style</span>
        }
        @if (isDefault) {
          <mat-checkbox formControlName="applyToAll">Apply to all features (ignore rule labels)</mat-checkbox>
        }
      </div>

      @if (!isDefault && labelAppliesToAll) {
        @if (form.get('default').get('text').get('enabled').value) {
          <span class="mat-body-small text-muted mt-1">{{ defaultTextSummary() }}</span>
        }
      } @else if (group.get('text').get('enabled').value) {
        <div class="row g-2 align-items-center mt-1">
          <div class="col-auto">
            <lib-property-tree-picker
              [nodes]="propertyTree"
              [selectedPath]="group.get('text').get('propertyPath').value"
              placeholder="Select label property"
              (pathSelected)="onTextPropertySelected(group, $event)" />
          </div>

          <div class="col-auto">
            <mat-form-field appearance="outline" class="form-ultra-slim field-width-lg" subscriptSizing="dynamic">
              <mat-label>Font</mat-label>
              <mat-select formControlName="fontStyle">
                @for (opt of fontStyleOptions; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div class="col-auto">
            <mat-form-field appearance="outline" class="form-ultra-slim field-width" subscriptSizing="dynamic">
              <mat-label>Size</mat-label>
              <input matInput type="number" formControlName="fontSize" min="1" />
            </mat-form-field>
          </div>

          <div class="col-auto d-flex align-items-center gap-1">
            <span class="mat-body-small">Color</span>
            <lib-color-swatch-picker
              [color]="group.get('text').get('fillColor').value"
              outputFormat="hex"
              [presetColors]="usedColors"
              (colorChange)="group.get('text').get('fillColor').setValue($event)"
              (colorApply)="addToUsedColors($event)" />
          </div>

          <div class="col-auto d-flex align-items-center gap-1">
            <span class="mat-body-small">Halo</span>
            <lib-color-swatch-picker
              [color]="group.get('text').get('haloColor').value"
              outputFormat="hex"
              [presetColors]="usedColors"
              (colorChange)="group.get('text').get('haloColor').setValue($event)"
              (colorApply)="addToUsedColors($event)" />
          </div>

          <div class="col-auto">
            <mat-form-field appearance="outline" class="form-ultra-slim field-width" subscriptSizing="dynamic">
              <mat-label>Halo width</mat-label>
              <input matInput type="number" formControlName="haloWidth" min="0" />
            </mat-form-field>
          </div>
        </div>
      }
    </div>

    @if (hasPointFeatures) {
      <div class="style-section" [formGroup]="group.get('icon')">
        <mat-checkbox formControlName="enabled">Icon</mat-checkbox>

        @if (group.get('icon').get('enabled').value) {
          <div class="row g-2 align-items-center mt-1">
            <div class="col-auto">
              <lib-icon-style-picker
                [selectedKey]="group.get('icon').get('key').value"
                [tintColor]="group.get('icon').get('color').value ?? '#000000'"
                (keySelected)="onIconKeySelected(group, $event)" />
            </div>

            <div class="col-auto">
              <mat-form-field appearance="outline" class="form-ultra-slim field-width" subscriptSizing="dynamic">
                <mat-label>Scale</mat-label>
                <input matInput type="number" formControlName="scale" min="0.1" step="0.1" />
              </mat-form-field>
            </div>

            <div class="col-auto">
              <mat-form-field appearance="outline" class="form-ultra-slim field-width" subscriptSizing="dynamic">
                <mat-label>Rotation</mat-label>
                <input matInput type="number" formControlName="rotation" />
              </mat-form-field>
            </div>

            <div class="col-auto d-flex align-items-center gap-1">
              <span class="mat-body-small">Tint</span>
              <lib-color-swatch-picker
                [color]="group.get('icon').get('color').value ?? '#000000'"
                outputFormat="hex"
                [presetColors]="usedColors"
                (colorChange)="group.get('icon').get('color').setValue($event)"
                (colorApply)="addToUsedColors($event)" />
            </div>
          </div>
        }
      </div>
    }
  </div>
</ng-template>

<h2 mat-dialog-title>Edit Style</h2>

<mat-dialog-content>
  <form [formGroup]="form" class="d-flex flex-column gap-3">
    <mat-card appearance="outlined">
      <mat-card-content class="d-flex flex-column gap-2">
        <span class="mat-title-medium">Default style</span>
        <span class="mat-body-small text-muted">Applied to every feature, and to any feature not matched by a rule below</span>
        <ng-container *ngTemplateOutlet="styleProps; context: { group: form.controls.default, isDefault: true }" />
      </mat-card-content>
    </mat-card>

    @if (rules.length) {
      <span class="mat-title-medium">Rules</span>
    }

    @for (rule of rules.controls; track rule; let i = $index) {
      <mat-card appearance="outlined" [formGroup]="rule">
        <mat-card-content class="d-flex flex-column gap-2">
          <span class="mat-title-small">Rule {{ i + 1 }}</span>

          @if (rule.get('unsupported').value) {
            <div class="unsupported-filter-banner">
              <span class="mat-body-small">Custom filter (not editable here):</span>
              <pre class="mat-body-small">{{ rule.get('rawFilter').value | json }}</pre>
            </div>
          } @else {
            @let conditions = conditionsOf(rule);
            <div class="d-flex flex-column gap-2">
              @if (conditions.length > 1) {
                <mat-form-field appearance="outline" class="form-ultra-slim field-width-lg" subscriptSizing="dynamic">
                  <mat-label>Match</mat-label>
                  <mat-select formControlName="combinator">
                    <mat-option value="all">All of (AND)</mat-option>
                    <mat-option value="any">Any of (OR)</mat-option>
                  </mat-select>
                </mat-form-field>
              }

              <div formArrayName="conditions" class="d-flex flex-column gap-2">
                @for (leaf of conditions.controls; track leaf; let ci = $index) {
                  @let valueInfo = getLeafValueInfo(leaf);
                  <div class="condition-row row g-2 align-items-center flex-nowrap" [formGroupName]="ci">
                    <div class="col-auto">
                      <lib-property-tree-picker
                        [nodes]="propertyTree"
                        [selectedPath]="leaf.get('propertyPath').value"
                        (pathSelected)="onLeafPropertySelected(leaf, $event)" />
                    </div>

                    <div class="col-auto">
                      <mat-form-field appearance="outline" class="form-ultra-slim field-width-lg" subscriptSizing="dynamic">
                        <mat-label>Operator</mat-label>
                        <mat-select formControlName="operator">
                          @for (op of operators; track op.value) {
                            <mat-option [value]="op.value">{{ op.label }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    </div>

                    <div class="col-auto">
                      <lib-value-input-widget
                        [kind]="valueInfo.kind"
                        [options]="valueInfo.options"
                        [operator]="leaf.get('operator').value"
                        [value]="leaf.get('value').value"
                        (valueChange)="leaf.get('value').setValue($event)" />
                    </div>

                    <div class="col-auto">
                      <button matIconButton type="button" (click)="removeCondition(rule, ci)" matTooltip="Remove condition">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  </div>
                }
              </div>

              <button matButton type="button" (click)="addCondition(rule)" class="align-self-start">
                <mat-icon>add</mat-icon>
                Add condition
              </button>
            </div>
          }

          <ng-container *ngTemplateOutlet="styleProps; context: { group: rule.get('styleProps'), isDefault: false }" />
        </mat-card-content>
        <mat-card-actions align="end">
          <button matButton type="button" (click)="removeRule(i)">
            <mat-icon>delete</mat-icon>
            Delete rule
          </button>
        </mat-card-actions>
      </mat-card>
    }

    <button matButton type="button" (click)="addRule()" class="align-self-start">
      <mat-icon>add</mat-icon>
      Add rule
    </button>
  </form>
</mat-dialog-content>

@let valid = isFormValid;
<mat-dialog-actions>
  <button matButton (click)="close()" mat-dialog-close>Cancel</button>
  <button
    matButton="filled"
    color="primary"
    [matTooltip]="saveDisabledReason"
    [matTooltipDisabled]="valid"
    [disabled]="!valid"
    (click)="save()">
    Save
  </button>
</mat-dialog-actions>
